import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, Eye, Play, Images, Trash2,
  Loader2, Upload, Pencil, X, Link2, Youtube,
} from "lucide-react";import { fileUrl, api, formatApiErrorDetail } from "@/lib/api";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";

const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v|ogg|ogv)$/i.test(url || "");
const isVideoFile = (file) => !!file && (
  (file.type && file.type.startsWith("video/"))
  || /\.(mp4|webm|mov|m4v|ogg|ogv|avi|mkv)$/i.test(file.name || "")
);

const UPLOAD_TIMEOUT_MS = 90000; // 90s hard limit — never spin forever

/**
 * Hook that owns the upload state for a single slot.
 *
 * Why this exists as a standalone helper instead of being inlined: the same
 * upload flow is used by both filled tiles and empty placeholders, AND each
 * caller needs the same error handling, timeout, progress, and cancel
 * support. Repeating it inline three times in v22 led to "stuck spinner"
 * bugs because a stalled axios request had no timeout and no AbortController.
 *
 * Now:
 *  - Hard 90s axios timeout — the request always resolves or rejects.
 *  - AbortController so the user can cancel mid-upload.
 *  - onUploadProgress → live percentage shown in the spinner overlay.
 *  - Friendly error messages that actually tell the user what went wrong.
 */
function useSlotUpload({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const ctrlRef = useRef(null);

  const cancel = () => {
    if (ctrlRef.current) {
      try { ctrlRef.current.abort(); } catch { /* ignore */ }
    }
    setBusy(false);
    setProgress(0);
  };

  const upload = async (file) => {
    if (!file || busy) return null;
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: ctrl.signal,
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      await onUploaded?.(data);
      setProgress(100);
      return data;
    } catch (err) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        toast("Upload cancelled.");
      } else if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
        toast.error(`Upload timed out after ${UPLOAD_TIMEOUT_MS / 1000}s. For videos, paste a YouTube or Vimeo link instead — it's instant and never times out.`);
      } else {
        // eslint-disable-next-line no-console
        console.error("Slot upload failed:", err.response?.status, err.response?.data, err.message);
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload failed: ${err.message || "network error"}`);
      }
      return null;
    } finally {
      setBusy(false);
      ctrlRef.current = null;
    }
  };

  return { busy, progress, upload, cancel };
}

/**
 * Compact gallery tile — image OR video poster (never autoplays in feed).
 *
 * Admin sees a 3-icon overlay anchored to the corners of each tile:
 *   • TOP-MIDDLE  → Edit details (location, description, SEO, etc.)
 *   • BOTTOM-LEFT → Direct upload (image) or YouTube/Vimeo paste (video).
 *   • BOTTOM-RIGHT → Delete this post.
 */
export default function GalleryTile({ project, onChanged }) {
  const { editing, isAdmin } = useAdmin();
  const navigate = useNavigate();
  const uploadRef = useRef(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [addingEmbed, setAddingEmbed] = useState(false);

  const rawCover = project.cover_image || project.media?.[0]?.url;
  const firstMedia = project.media?.[0];
  const isCoverVideoUrl = isVideoUrl(rawCover);
  const firstIsVideo = firstMedia?.type === "video" || isVideoUrl(firstMedia?.url);
  const posterFromMedia = firstMedia?.poster_url;
  const thumbUrl = isCoverVideoUrl ? (posterFromMedia || rawCover) : rawCover;
  const showPlayBadge = !!project.is_video_project || isCoverVideoUrl || firstIsVideo;
  const hasMultiple = (project.media?.length || 0) > 1;

  const stop = (fn) => (e) => { e?.preventDefault?.(); e?.stopPropagation?.(); return fn?.(e); };

  const initialIsProcessing = isAdmin
    && !!(firstMedia?.processing)
    && (firstMedia?.type === "video" || isVideoUrl(firstMedia?.url));
  const [isProcessingVideo, setIsProcessingVideo] = useState(initialIsProcessing);
  useEffect(() => {
    setIsProcessingVideo(initialIsProcessing);
    if (!initialIsProcessing) return undefined;
    const t = setTimeout(() => setIsProcessingVideo(false), 30000);
    return () => clearTimeout(t);
  }, [initialIsProcessing]);

  const applyUploadToSlot = async (uploaded) => {
    const newItem = {
      url: uploaded.url,
      type: uploaded.media_type,
      ...(uploaded.poster_url ? { poster_url: uploaded.poster_url } : {}),
      ...(uploaded.id ? { file_id: uploaded.id } : {}),
      ...(uploaded.processing ? { processing: true } : {}),
    };
    const newMedia = [newItem, ...(project.media?.slice(1) || [])];
    const newCover = uploaded.media_type === "image"
      ? uploaded.url
      : (uploaded.poster_url || uploaded.url);
    await api.patch(`/projects/${project.id}`, {
      media: newMedia,
      cover_image: newCover,
      is_video_project: uploaded.media_type === "video",
    });
    toast.success("Slot updated");
    onChanged?.();
  };

  const { busy: uploading, progress, upload, cancel } = useSlotUpload({
    onUploaded: applyUploadToSlot,
  });

  const goToEdit = stop(() => navigate(`/admin/projects/edit/${project.id}`));
  const triggerUpload = stop(() => uploadRef.current?.click());

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // Upload everything (images AND videos) directly via /api/upload.
    // The 90s timeout + progress + cancel UI gives the user real feedback;
    // no more forced YouTube/Vimeo paste detour.
    await upload(file);
  };

  const addEmbed = async () => {
    const url = embedUrl.trim();
    if (!url) return;
    setAddingEmbed(true);
    try {
      const { data: embedItem } = await api.post("/media/embed", { url });
      const newMedia = [embedItem, ...(project.media?.slice(1) || [])];
      await api.patch(`/projects/${project.id}`, {
        media: newMedia,
        cover_image: embedItem.poster_url || embedItem.url,
        is_video_project: true,
      });
      toast.success("Video added — no upload needed");
      setEmbedUrl("");
      setEmbedOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse that link.");
    } finally {
      setAddingEmbed(false);
    }
  };

  const remove = stop(async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  });

  return (
    <Link
      to={`/project/${project.slug}`}
      data-testid={`gallery-tile-${project.slug}`}
      className="group relative block overflow-hidden bg-[color:var(--bda-surface)] aspect-square cursor-pointer"
    >
      <img
        src={fileUrl(thumbUrl)}
        alt={project.title || project.location}
        loading="lazy"
        className="zoom-img w-full h-full object-cover"
      />

      <div className="absolute inset-0 transition-opacity duration-500 ease-out opacity-0 group-hover:opacity-100 bg-black/55" />

      {(showPlayBadge || hasMultiple) && !isProcessingVideo && !editing && (
        <div className="absolute top-2.5 right-2.5 text-white drop-shadow-md">
          {showPlayBadge ? <Play className="w-[18px] h-[18px] fill-current" /> : <Images className="w-[18px] h-[18px]" strokeWidth={1.5} />}
        </div>
      )}

      {isProcessingVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 text-white z-[1] pointer-events-none">
          <Loader2 className="w-6 h-6 animate-spin mb-1.5" />
          <span className="text-[9px] tracking-[0.3em] uppercase">Processing</span>
        </div>
      )}

      {showPlayBadge && !isProcessingVideo && !editing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white ml-0.5" />
          </div>
        </div>
      )}

      {!editing && (
        <div className="absolute inset-0 flex items-center justify-center gap-6 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <span className="inline-flex items-center gap-1.5">
            <Heart className="w-[18px] h-[18px] fill-current" /> {project.like_count || 0}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Eye className="w-[18px] h-[18px]" strokeWidth={1.6} /> {project.view_count || 0}
          </span>
        </div>
      )}

      {/* ADMIN OVERLAY */}
      {editing && (
        <>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*,video/*"
            onChange={onPickFile}
            data-testid={`tile-${project.slug}-file-input`}
            className="hidden"
          />

          <div className="absolute top-1.5 left-1.5 flex flex-col gap-1 z-10 pointer-events-none">
            {!project.is_published && (
              <span className="text-[9px] tracking-widest uppercase bg-amber-400/95 text-black px-1.5 py-0.5 rounded">Draft</span>
            )}
            {project.is_archived && (
              <span className="text-[9px] tracking-widest uppercase bg-slate-700/95 text-white px-1.5 py-0.5 rounded">Archived</span>
            )}
            {project.is_pinned && (
              <span className="text-[9px] tracking-widest uppercase bg-[color:var(--bda-blue-glow)]/95 text-black px-1.5 py-0.5 rounded">Pinned</span>
            )}
          </div>

          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 z-10">
            <ToolBtn onClick={goToEdit} title="Edit details" testId={`tile-${project.slug}-edit`} tone="white">
              <Pencil className="w-4 h-4" />
            </ToolBtn>
          </div>

          <div className="absolute inset-x-0 bottom-0 z-10 px-2 pb-2 pt-10 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end justify-between pointer-events-none">
            <ToolBtn onClick={triggerUpload} title="Upload media" testId={`tile-${project.slug}-upload`} tone="white" busy={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </ToolBtn>
            <ToolBtn onClick={remove} title="Delete" testId={`tile-${project.slug}-delete`} tone="danger">
              <Trash2 className="w-4 h-4" />
            </ToolBtn>
          </div>

          <UploadingOverlay uploading={uploading} progress={progress} onCancel={cancel} />

          {embedOpen && (
            <EmbedPasteOverlay
              embedUrl={embedUrl}
              setEmbedUrl={setEmbedUrl}
              onAdd={addEmbed}
              onClose={() => { setEmbedOpen(false); setEmbedUrl(""); }}
              adding={addingEmbed}
              testIdPrefix={`tile-${project.slug}`}
            />
          )}
        </>
      )}
    </Link>
  );
}

function UploadingOverlay({ uploading, progress, onCancel }) {
  if (!uploading) return null;
  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-white z-20 px-3" data-testid="upload-overlay">
      <Loader2 className="w-7 h-7 animate-spin mb-2" />
      <span className="text-[10px] tracking-[0.3em] uppercase">Uploading {progress}%</span>
      <div className="w-full max-w-[140px] h-1 bg-white/20 rounded-full overflow-hidden mt-2">
        <div className="h-full bg-white transition-all duration-200" style={{ width: `${progress}%` }} />
      </div>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel?.(); }}
        data-testid="upload-cancel"
        className="mt-3 px-3 py-1 rounded-full bg-white/15 hover:bg-white/25 text-[10px] tracking-[0.2em] uppercase pointer-events-auto"
      >
        Cancel
      </button>
    </div>
  );
}

function EmbedPasteOverlay({ embedUrl, setEmbedUrl, onAdd, onClose, adding, testIdPrefix }) {
  return (
    <div
      className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center text-white z-30 px-3 py-4"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      data-testid={`${testIdPrefix}-embed-overlay`}
    >
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
        data-testid={`${testIdPrefix}-embed-close`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <Youtube className="w-6 h-6 text-emerald-400 mb-1.5" />
      <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 mb-1">Use a YouTube / Vimeo link</div>
      <div className="text-[10px] text-white/70 mb-2.5 text-center leading-snug">Plays instantly — no upload, no waiting.</div>
      <input
        type="url"
        value={embedUrl}
        onChange={(e) => setEmbedUrl(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        placeholder="Paste link…"
        data-testid={`${testIdPrefix}-embed-input`}
        className="w-full max-w-[180px] px-2.5 py-1.5 rounded-md text-[12px] bg-white text-slate-900 placeholder:text-slate-400"
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
      />
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAdd(); }}
        disabled={adding || !embedUrl.trim()}
        data-testid={`${testIdPrefix}-embed-add`}
        className="mt-2 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-[11px] font-medium pointer-events-auto"
      >
        <Link2 className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Add video"}
      </button>
    </div>
  );
}

function ToolBtn({ onClick, title, children, tone = "white", testId, busy = false, disabled = false }) {
  const cls = tone === "danger"
    ? "bg-red-600/95 hover:bg-red-600 text-white"
    : "bg-white/95 hover:bg-white text-slate-900 border border-slate-200";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      disabled={disabled || busy}
      className={`w-10 h-10 rounded-full shadow-lg backdrop-blur flex items-center justify-center transition-all active:scale-90 pointer-events-auto disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}

/**
 * Empty placeholder used to keep the grid uniform. Visible to admins only.
 * Image upload creates a new project at the slot's display_order; video
 * picks open a YouTube/Vimeo paste overlay instead so the pod is never
 * involved in video traffic.
 */
export function EmptyTile({ slotIndex = 0, onUploaded }) {
  const { editing } = useAdmin();
  const uploadRef = useRef(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [addingEmbed, setAddingEmbed] = useState(false);

  const stopE = (e) => { e?.preventDefault?.(); e?.stopPropagation?.(); };

  const createProjectFromUpload = async (uploaded) => {
    const mediaItem = {
      url: uploaded.url,
      type: uploaded.media_type,
      ...(uploaded.poster_url ? { poster_url: uploaded.poster_url } : {}),
      ...(uploaded.id ? { file_id: uploaded.id } : {}),
      ...(uploaded.processing ? { processing: true } : {}),
    };
    const cover = uploaded.media_type === "image"
      ? uploaded.url
      : (uploaded.poster_url || uploaded.url);
    await api.post("/projects", {
      location: "Untitled",
      description: "",
      media: [mediaItem],
      cover_image: cover,
      is_video_project: uploaded.media_type === "video",
      is_published: true,
      display_order: slotIndex,
    });
    toast.success("Posted — tap the pencil to add title and details");
    onUploaded?.();
  };

  const { busy: uploading, progress, upload, cancel } = useSlotUpload({
    onUploaded: createProjectFromUpload,
  });

  if (!editing) return null;

  const triggerUpload = (e) => { stopE(e); uploadRef.current?.click(); };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // Upload everything (images AND videos) directly — no forced detour.
    await upload(file);
  };

  const addEmbed = async () => {
    const url = embedUrl.trim();
    if (!url) return;
    setAddingEmbed(true);
    try {
      const { data: embedItem } = await api.post("/media/embed", { url });
      await api.post("/projects", {
        location: "Untitled",
        description: "",
        media: [embedItem],
        cover_image: embedItem.poster_url || embedItem.url,
        is_video_project: true,
        is_published: true,
        display_order: slotIndex,
      });
      toast.success("Video posted — tap the pencil to add title and details");
      setEmbedUrl("");
      setEmbedOpen(false);
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not parse that link.");
    } finally {
      setAddingEmbed(false);
    }
  };

  return (
    <div
      data-testid={`empty-tile-${slotIndex}`}
      className="relative aspect-square w-full bg-[color:var(--bda-surface-2)]/60 border border-dashed border-[color:var(--bda-border)] flex flex-col items-center justify-center gap-1 text-[color:var(--bda-text-dim)] overflow-hidden"
    >
      <input
        ref={uploadRef}
        type="file"
        accept="image/*,video/*"
        onChange={onPickFile}
        data-testid={`empty-tile-${slotIndex}-file-input`}
        className="hidden"
      />

      <span className="w-8 h-8 rounded-full border border-current flex items-center justify-center text-base">+</span>
      <span className="text-[9px] tracking-[0.3em] uppercase">Add Media</span>

      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 z-10">
        <ToolBtn
          title="Upload media first, then tap edit to add details"
          testId={`empty-tile-${slotIndex}-edit`}
          tone="white"
          disabled
        >
          <Pencil className="w-4 h-4" />
        </ToolBtn>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-10 px-2 pb-2 pt-10 bg-gradient-to-t from-black/30 to-transparent flex items-end justify-between pointer-events-none">
        <ToolBtn
          onClick={triggerUpload}
          title="Upload media to this slot"
          testId={`empty-tile-${slotIndex}-upload`}
          tone="white"
          busy={uploading}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </ToolBtn>
        <ToolBtn
          title="Delete (empty slot)"
          testId={`empty-tile-${slotIndex}-delete`}
          tone="danger"
          disabled
        >
          <Trash2 className="w-4 h-4" />
        </ToolBtn>
      </div>

      <UploadingOverlay uploading={uploading} progress={progress} onCancel={cancel} />

      {embedOpen && (
        <EmbedPasteOverlay
          embedUrl={embedUrl}
          setEmbedUrl={setEmbedUrl}
          onAdd={addEmbed}
          onClose={() => { setEmbedOpen(false); setEmbedUrl(""); }}
          adding={addingEmbed}
          testIdPrefix={`empty-tile-${slotIndex}`}
        />
      )}
    </div>
  );
}

export function LoadingTile() {
  return (
    <div className="aspect-square flex items-center justify-center bg-[color:var(--bda-surface-2)]/40">
      <Loader2 className="w-5 h-5 text-[color:var(--bda-text-dim)] animate-spin" />
    </div>
  );
}
